/**
 * The game's database in the browser (IndexedDB): the shelf of kept pieces (see shelf.ts),
 * the work in progress (see work.ts) and each request's record (see records.ts).
 */
const DB_NAME = "kneading-needed", VERSION = 3;

function open(): Promise<IDBDatabase> {
  return new Promise((done, fail) => {
    const req = indexedDB.open(DB_NAME, VERSION);
    req.onupgradeneeded = (e) => {
      const db = req.result;
      if (e.oldVersion < 1) db.createObjectStore("shelf", { keyPath: "id", autoIncrement: true });
      if (e.oldVersion < 2) db.createObjectStore("work");
      if (e.oldVersion < 3) db.createObjectStore("records");
    };
    req.onsuccess = () => done(req.result);
    req.onerror = () => fail(req.error);
  });
}

/** Runs `use` in one transaction over `stores`, settling once the transaction has. */
export async function transactAll(
  stores: string[],
  mode: IDBTransactionMode,
  use: (tx: IDBTransaction) => void,
): Promise<void> {
  const db = await open();
  try {
    await new Promise<void>((done, fail) => {
      const tx = db.transaction(stores, mode);
      tx.oncomplete = () => done();
      tx.onerror = () => fail(tx.error);
      tx.onabort = () => fail(tx.error);
      use(tx);
    });
  } finally {
    db.close();
  }
}

/** Runs `use` in one transaction on `store`, settling once the transaction has. */
export const transact = (
  store: string,
  mode: IDBTransactionMode,
  use: (s: IDBObjectStore) => void,
) => transactAll([store], mode, (tx) => use(tx.objectStore(store)));

/** One request in its own transaction, resolving with its result once committed. */
export async function request<T>(
  store: string,
  mode: IDBTransactionMode,
  use: (s: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  let result!: T;
  await transact(store, mode, (s) => {
    const req = use(s);
    req.onsuccess = () => result = req.result;
  });
  return result;
}
